import { ArrowDownUp, ChevronDown, ChevronUp, Search, X } from 'lucide-react';
import { useMemo, useState } from 'react';

import { usePartnerAccounts } from '@/api/partner-accounts';
import { useTaxonomy } from '@/api/taxonomy';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import {
  CONTACT_KINDS,
  CONTACT_KIND_LABELS,
  MISSING_FIELDS,
  MISSING_FIELD_LABELS,
  SORT_FIELDS,
  SORT_LABELS,
  clearedFilters,
  countActiveFilters,
} from '@/lib/synced-location-filters';
import type {
  ContactKindFilter,
  FacetCount,
  FacetGroup,
  MissingField,
  SyncedLocationFacets,
  SyncedLocationListParams,
  SyncedLocationSortField,
} from '@/types/api';

import { FilterMultiSelect, type FilterOption } from './FilterMultiSelect';

interface SyncedLocationFiltersProps {
  filters: SyncedLocationListParams;
  /** Counts per value for the current filter set; absent while loading. */
  facets?: SyncedLocationFacets;
  onFilterChange: (filters: Partial<SyncedLocationListParams>) => void;
  /** Replace the whole filter set (used by "Clear all"). */
  onReplaceFilters: (filters: SyncedLocationListParams) => void;
  /** Ask for a facet group that is only counted on demand (a menu opened). */
  onRequestFacet?: (group: FacetGroup) => void;
}

const IMAGE_BUCKETS: { value: string; label: string; min?: number; max?: number }[] = [
  { value: '0', label: 'No photos', min: 0, max: 0 },
  { value: '1-3', label: '1–3 photos', min: 1, max: 3 },
  { value: '4-9', label: '4–9 photos', min: 4, max: 9 },
  { value: '10+', label: '10+ photos', min: 10 },
];

const CAPACITY_BUCKETS: { value: string; label: string; min?: number; max?: number }[] = [
  { value: 'unknown', label: 'Unknown' },
  { value: '1-9', label: '1–9', min: 1, max: 9 },
  { value: '10-49', label: '10–49', min: 10, max: 49 },
  { value: '50-199', label: '50–199', min: 50, max: 199 },
  { value: '200+', label: '200+', min: 200 },
];

function withCounts(values: readonly string[] | undefined, facet: FacetCount[] | undefined, labels?: Record<string, string>): FilterOption[] {
  const counts = new Map((facet ?? []).map((f) => [f.value, f.count]));
  const known = values ?? (facet ?? []).map((f) => f.value);
  return known.map((value) => ({
    value,
    label: labels?.[value] ?? value,
    count: counts.get(value),
  }));
}

/** Which single bucket a min/max pair is, if it is exactly one of ours. */
function bucketFor(
  buckets: { value: string; min?: number; max?: number }[],
  min: number | undefined,
  max: number | undefined,
  unknown?: boolean,
): string[] {
  if (unknown === true && min === undefined && max === undefined) return ['unknown'];
  const hit = buckets.find((b) => b.min === min && b.max === max && b.value !== 'unknown');
  return hit ? [hit.value] : [];
}

type Tri = 'any' | 'yes' | 'no';

function triOf(value: boolean | undefined): Tri {
  return value === undefined ? 'any' : value ? 'yes' : 'no';
}

function triValue(v: string): boolean | undefined {
  return v === 'yes' ? true : v === 'no' ? false : undefined;
}

/** A three-way select: any / yes / no, for the boolean filters. */
function TriSelect({
  label, value, onChange, yes, no, testId,
}: { label: string; value: boolean | undefined; onChange: (v: boolean | undefined) => void; yes: string; no: string; testId: string }): JSX.Element {
  return (
    <Select value={triOf(value)} onValueChange={(v) => { onChange(triValue(v)); }}>
      <SelectTrigger className={`h-8 w-full text-sm md:w-44 ${value !== undefined ? 'bg-secondary' : ''}`} data-testid={testId}>
        <SelectValue placeholder={label} />
      </SelectTrigger>
      <SelectContent>
        <SelectItem value="any">{label}: any</SelectItem>
        <SelectItem value="yes">{yes}</SelectItem>
        <SelectItem value="no">{no}</SelectItem>
      </SelectContent>
    </Select>
  );
}

function DateRange({
  label, from, to, onChange, testId,
}: { label: string; from: string | undefined; to: string | undefined; onChange: (from: string | undefined, to: string | undefined) => void; testId: string }): JSX.Element {
  return (
    <div className="flex items-center gap-1 text-sm">
      <span className="text-muted-foreground">{label}</span>
      <Input type="date" value={from ?? ''} onChange={(e) => { onChange(e.target.value || undefined, to); }} className="h-8 w-36" data-testid={`${testId}-from`} />
      <span className="text-muted-foreground">–</span>
      <Input type="date" value={to ?? ''} onChange={(e) => { onChange(from, e.target.value || undefined); }} className="h-8 w-36" data-testid={`${testId}-to`} />
    </div>
  );
}

/**
 * The filter bar for 167,000 synced locations.
 *
 * Every control writes to the URL through `onFilterChange`, and every list
 * shows the facet counts for the CURRENT filter set, so a person narrowing
 * from "Germany" to "Berlin" to "Hotel/Motel" sees how many rows are left
 * before each click. The first row is what a scout reaches for daily; the
 * rest sits behind "More filters" so the common case stays one line.
 */
export function SyncedLocationFilters({
  filters,
  facets,
  onFilterChange,
  onReplaceFilters,
  onRequestFacet,
}: SyncedLocationFiltersProps): JSX.Element {
  const { data: accounts } = usePartnerAccounts();
  const { data: taxonomy } = useTaxonomy();
  const [searchInput, setSearchInput] = useState(filters.search ?? '');
  const [showMore, setShowMore] = useState(false);
  const active = countActiveFilters(filters);

  const commitSearch = (): void => {
    const next = searchInput.trim() || undefined;
    if (next !== filters.search) onFilterChange({ search: next });
  };

  const accountOptions = useMemo<FilterOption[]>(() => {
    const byId = new Map((accounts ?? []).map((a) => [a.id, a.name]));
    const ids = new Set<string>([...byId.keys(), ...(filters.partnerAccountIds ?? [])]);
    return [...ids]
      .map((id) => ({ value: id, label: byId.get(id) ?? `Account ${id}` }))
      .sort((a, b) => a.label.localeCompare(b.label));
  }, [accounts, filters.partnerAccountIds]);

  const typeOptions = useMemo<FilterOption[]>(() => {
    const names = new Map((taxonomy?.locationTypes ?? []).map((t) => [t.id, t.name]));
    const counts = new Map((facets?.locationType ?? []).map((f) => [f.value, f.count]));
    const ids = new Set<string>(['none', ...names.keys(), ...counts.keys(), ...(filters.locationTypeIds ?? [])]);
    return [...ids].map((id) => ({
      value: id,
      label: id === 'none' ? 'Not typed yet' : names.get(id) ?? `Type ${id}`,
      count: counts.get(id),
    }));
  }, [taxonomy, facets?.locationType, filters.locationTypeIds]);

  const imageBucket = bucketFor(IMAGE_BUCKETS, filters.minImages, filters.maxImages);
  const capacityBucket = bucketFor(CAPACITY_BUCKETS, filters.minCapacity, filters.maxCapacity, filters.capacityUnknown);

  const setImageBucket = (values: string[]): void => {
    const b = IMAGE_BUCKETS.find((x) => x.value === values[values.length - 1]);
    onFilterChange({ minImages: b?.min, maxImages: b?.max });
  };
  const setCapacityBucket = (values: string[]): void => {
    const last = values[values.length - 1];
    if (last === 'unknown') {
      onFilterChange({ minCapacity: undefined, maxCapacity: undefined, capacityUnknown: true });
      return;
    }
    const b = CAPACITY_BUCKETS.find((x) => x.value === last);
    onFilterChange({ minCapacity: b?.min, maxCapacity: b?.max, capacityUnknown: undefined });
  };

  const sortValue = `${filters.sortBy ?? 'createdAt'}:${filters.sortDir ?? (filters.sortBy === 'name' || filters.sortBy === 'city' ? 'asc' : 'desc')}`;

  return (
    <div className="space-y-3" data-testid="synced-location-filters">
      {/* Row 1: search, sort, clear */}
      <div className="flex flex-wrap items-center gap-2">
        <div className="relative w-full md:w-80">
          <Search className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
          <Input
            placeholder="Search name, description, address, id…"
            value={searchInput}
            onChange={(e) => { setSearchInput(e.target.value); }}
            onKeyDown={(e) => { if (e.key === 'Enter') commitSearch(); }}
            onBlur={commitSearch}
            className="pl-9"
            data-testid="location-search-input"
          />
        </div>

        <Select
          value={sortValue}
          onValueChange={(v) => {
            const [field, dir] = v.split(':') as [SyncedLocationSortField, 'asc' | 'desc'];
            onFilterChange({ sortBy: field, sortDir: dir });
          }}
        >
          <SelectTrigger className="h-9 w-full md:w-52" data-testid="sort-select">
            <ArrowDownUp className="mr-1 h-3.5 w-3.5 text-muted-foreground" />
            <SelectValue />
          </SelectTrigger>
          <SelectContent>
            {SORT_FIELDS.flatMap((f) => [
              <SelectItem key={`${f}:desc`} value={`${f}:desc`}>{SORT_LABELS[f]} ↓</SelectItem>,
              <SelectItem key={`${f}:asc`} value={`${f}:asc`}>{SORT_LABELS[f]} ↑</SelectItem>,
            ])}
          </SelectContent>
        </Select>

        {facets !== undefined && (
          <span className="text-sm text-muted-foreground" data-testid="facet-total">
            {facets.total.toLocaleString()} match{facets.total === 1 ? '' : 'es'}
          </span>
        )}

        {active > 0 && (
          <Button variant="ghost" size="sm" onClick={() => { setSearchInput(''); onReplaceFilters(clearedFilters(filters)); }} data-testid="clear-filters">
            <X className="h-3.5 w-3.5" />
            Clear all ({active})
          </Button>
        )}
      </div>

      {/* Row 2: the filters a scout reaches for daily */}
      <div className="flex flex-wrap items-center gap-2">
        <FilterMultiSelect label="Account" options={accountOptions} selected={filters.partnerAccountIds ?? []} onChange={(v) => { onFilterChange({ partnerAccountIds: v }); }} testId="partner-account-filter" searchable />
        <FilterMultiSelect label="Source" options={withCounts(undefined, facets?.source)} selected={filters.sources ?? []} onChange={(v) => { onFilterChange({ sources: v }); }} testId="source-filter" loading={facets === undefined} />
        <FilterMultiSelect label="Country" options={withCounts(undefined, facets?.country)} selected={filters.countries ?? []} onChange={(v) => { onFilterChange({ countries: v }); }} testId="country-filter" loading={facets === undefined} />
        <FilterMultiSelect label="City" options={withCounts(undefined, facets?.city)} selected={filters.cities ?? []} onChange={(v) => { onFilterChange({ cities: v }); }} testId="city-filter" loading={facets === undefined} hint="The 100 most common in the current filter" />
        <FilterMultiSelect label="Category" onOpen={() => onRequestFacet?.('category')} options={withCounts(undefined, facets?.category)} selected={filters.categories ?? []} onChange={(v) => { onFilterChange({ categories: v }); }} testId="category-filter" loading={facets === undefined} hint="Parent types count every subtype" />
        <FilterMultiSelect label="Location type" onOpen={() => onRequestFacet?.('locationType')} options={typeOptions} selected={filters.locationTypeIds ?? []} onChange={(v) => { onFilterChange({ locationTypeIds: v }); }} testId="location-type-filter" searchable />
        <FilterMultiSelect label="Missing" onOpen={() => onRequestFacet?.('missing')} options={withCounts(MISSING_FIELDS, facets?.missing, MISSING_FIELD_LABELS)} selected={filters.missing ?? []} onChange={(v) => { onFilterChange({ missing: v as MissingField[] }); }} testId="missing-filter" />
        <FilterMultiSelect label="Contact" onOpen={() => onRequestFacet?.('contact')} options={withCounts(CONTACT_KINDS, facets?.contact, CONTACT_KIND_LABELS)} selected={filters.contacts ?? []} onChange={(v) => { onFilterChange({ contacts: v as ContactKindFilter[] }); }} testId="contact-filter" />
        <Button variant="ghost" size="sm" onClick={() => { setShowMore((s) => !s); }} data-testid="more-filters-toggle">
          {showMore ? <ChevronUp className="h-3.5 w-3.5" /> : <ChevronDown className="h-3.5 w-3.5" />}
          More filters
        </Button>
      </div>

      {showMore && (
        <div className="space-y-2 rounded-lg border bg-muted/30 p-3" data-testid="more-filters">
          <div className="flex flex-wrap items-center gap-2">
            <FilterMultiSelect label="Asset class" options={withCounts(undefined, facets?.assetClass, { location: 'Location', poi: 'Point of interest' })} selected={filters.assetClasses ?? []} onChange={(v) => { onFilterChange({ assetClasses: v }); }} testId="asset-class-filter" />
            <FilterMultiSelect label="Amenity" onOpen={() => onRequestFacet?.('amenity')} options={withCounts(undefined, facets?.amenity)} selected={filters.amenities ?? []} onChange={(v) => { onFilterChange({ amenities: v }); }} testId="amenity-filter" loading={facets === undefined} />
            <FilterMultiSelect label="Tag" onOpen={() => onRequestFacet?.('tag')} options={withCounts(undefined, facets?.tag)} selected={filters.tags ?? []} onChange={(v) => { onFilterChange({ tags: v }); }} testId="tag-filter" loading={facets === undefined} />
            <FilterMultiSelect label="Photos" options={withCounts(IMAGE_BUCKETS.map((b) => b.value), facets?.images, Object.fromEntries(IMAGE_BUCKETS.map((b) => [b.value, b.label])))} selected={imageBucket} onChange={setImageBucket} testId="images-filter" />
            <FilterMultiSelect label="Capacity" options={withCounts(CAPACITY_BUCKETS.map((b) => b.value), facets?.capacity, Object.fromEntries(CAPACITY_BUCKETS.map((b) => [b.value, b.label])))} selected={capacityBucket} onChange={setCapacityBucket} testId="capacity-filter" />
            <FilterMultiSelect label="Title" options={[{ value: 'partner', label: 'Partner title' }, { value: 'ai', label: 'AI title' }]} selected={filters.titleSource ? [filters.titleSource] : []} onChange={(v) => { const last = v[v.length - 1]; onFilterChange({ titleSource: last === 'partner' || last === 'ai' ? last : undefined }); }} testId="title-source-filter" />
          </div>
          <div className="flex flex-wrap items-center gap-2">
            <Select value={filters.enrichmentStatus ?? 'all'} onValueChange={(v) => { onFilterChange({ enrichmentStatus: v === 'all' ? undefined : (v as SyncedLocationListParams['enrichmentStatus']) }); }}>
              <SelectTrigger className="h-8 w-full md:w-40" data-testid="enrichment-status-filter"><SelectValue placeholder="All Enrichment" /></SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All Enrichment</SelectItem>
                <SelectItem value="pending">Pending</SelectItem>
                <SelectItem value="enriched">Enriched</SelectItem>
              </SelectContent>
            </Select>
            <Select value={filters.publicationStatus ?? 'all'} onValueChange={(v) => { onFilterChange({ publicationStatus: v === 'all' ? undefined : (v as SyncedLocationListParams['publicationStatus']) }); }}>
              <SelectTrigger className="h-8 w-full md:w-44" data-testid="publication-status-filter"><SelectValue placeholder="All Publication" /></SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All Publication</SelectItem>
                <SelectItem value="not_published">Not Published</SelectItem>
                <SelectItem value="pending_review">Pending Review</SelectItem>
                <SelectItem value="published">Published</SelectItem>
                <SelectItem value="rejected">Rejected</SelectItem>
                <SelectItem value="unpublished">Unpublished</SelectItem>
              </SelectContent>
            </Select>
            <TriSelect label="Ready to publish" value={filters.readyToPublish} onChange={(v) => { onFilterChange({ readyToPublish: v }); }} yes="Ready to publish" no="Not ready" testId="ready-to-publish-filter" />
            <TriSelect label="Blocked" value={filters.isBlocked} onChange={(v) => { onFilterChange({ isBlocked: v }); }} yes="Blocked" no="Not blocked" testId="blocked-status-filter" />
            <TriSelect label="Duplicate group" value={filters.inGroup} onChange={(v) => { onFilterChange({ inGroup: v }); }} yes="In a duplicate group" no="Not in a group" testId="in-group-filter" />
            <TriSelect label="Repeated name" value={filters.duplicateName} onChange={(v) => { onFilterChange({ duplicateName: v }); }} yes="Name repeats in its city" no="Name is unique in its city" testId="duplicate-name-filter" />
            <TriSelect label="Address vs pin" value={filters.addressMatchesPin} onChange={(v) => { onFilterChange({ addressMatchesPin: v }); }} yes="Address agrees with pin" no="Address disagrees with pin" testId="address-pin-filter" />
            <TriSelect label="Price" value={filters.hasPrice} onChange={(v) => { onFilterChange({ hasPrice: v }); }} yes="Has a price" no="No price" testId="has-price-filter" />
            <TriSelect label="Owner" value={filters.hasOwner} onChange={(v) => { onFilterChange({ hasOwner: v }); }} yes="Owner assigned" no="No owner" testId="has-owner-filter" />
            <TriSelect label="Live listing" value={filters.hasPublished} onChange={(v) => { onFilterChange({ hasPublished: v }); }} yes="Has a ScoutAround listing" no="Never published" testId="has-published-filter" />
            <TriSelect label="AI content" value={filters.aiContentFailed} onChange={(v) => { onFilterChange({ aiContentFailed: v }); }} yes="AI content failed" no="AI content not failed" testId="ai-failed-filter" />
          </div>
          <div className="flex flex-wrap items-center gap-4">
            <DateRange label="Imported" from={filters.createdFrom} to={filters.createdTo} onChange={(from, to) => { onFilterChange({ createdFrom: from, createdTo: to }); }} testId="created" />
            <DateRange label="Synced" from={filters.syncedFrom} to={filters.syncedTo} onChange={(from, to) => { onFilterChange({ syncedFrom: from, syncedTo: to }); }} testId="synced" />
            <DateRange label="Updated" from={filters.updatedFrom} to={filters.updatedTo} onChange={(from, to) => { onFilterChange({ updatedFrom: from, updatedTo: to }); }} testId="updated" />
          </div>
          {(filters.bbox !== undefined || filters.near !== undefined) && (
            <div className="flex items-center gap-2 text-sm">
              <Badge variant="secondary">Map area {filters.bbox ?? `${filters.near ?? ''} ±${String(filters.radiusKm ?? 5)} km`}</Badge>
              <Button variant="ghost" size="sm" onClick={() => { onFilterChange({ bbox: undefined, near: undefined, radiusKm: undefined }); }} data-testid="clear-map-filter">
                <X className="h-3.5 w-3.5" /> Remove
              </Button>
            </div>
          )}
        </div>
      )}
    </div>
  );
}
